/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IHelp, IOrderSummary, IPaymentMethods } from '@msdyn365-commerce-modules/order-management';
import { IGroup, IGroupDelivery, IGroups } from '@msdyn365-commerce-modules/order-management';
import { IOrderInformation } from '@msdyn365-commerce-modules/order-management';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management';
import { IOrderDetailsViewProps } from '@msdyn365-commerce-modules/order-management';
import { IOrderDetailsProps, ITrackingLinksData } from '../definition-extensions/order-details.ext.props.autogenerated';

import { attrNames } from '../../../utilities/global-constants';

//----------------------------------------------------------
// @CUSTOM: Imports for customizations
//----------------------------------------------------------
import { clsHelper } from '../../../utilities/class-name-helper';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';
//----------------------------------------------------------

//----------------------------------------------------------
// @CUSTOM: Class name utility for customizations that
// want to use the same base class
//----------------------------------------------------------
const BASE_CLASS = 'ms-order-details';
const cls = (fragment?: string) => clsHelper(BASE_CLASS, fragment);
//----------------------------------------------------------

export const OrderDetailsOrderInfomation: React.FC<IOrderInformation & {poNumber?: string}> = ({
    orderInformationProps,
    salesId,
    receiptId,
    createdDate,
    count,
    amount,
    channelReferenceId,
    channelName,
    channelAddress,
    note,
    poNumber
}) => (
    <Node {...orderInformationProps}>
        {channelName}
        {channelAddress}
        {channelReferenceId}
        {salesId}
        {receiptId}
        {createdDate}
        {count}
        {amount}
        {note}
        {poNumber && <OrderDetailsPONumber poNumber={poNumber} />}
    </Node>
);

export const OrderDetailsSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton, errors }) => (
    <Node {...salesLineProps}>
        {salesLine}
        {buyAgainButton}
        {errors}
    </Node>
);

export const OrderDetailsGroupDelivery: React.FC<IGroupDelivery> = ({ deliveryProps, heading, count }) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
    </Node>
);

export const OrderDetailsGroup: React.FC<IGroup & { trackingLinks?: ITrackingLinksData[]; poNumber?: string }> = ({
    groupProps,
    delivery,
    address,
    salesLinesProps,
    salesLines,
    isCashAndCarryTransaction,
    trackingLinks,
    poNumber
}) => (
    <Node {...groupProps}>
        {delivery && <OrderDetailsGroupDelivery {...delivery} />}
        <div className={cls('table-container')}>
            <table className={cls('table')}>
                <thead>
                    <tr>
                        <th>{attrNames.itemNumber}</th>
                        <th>{attrNames.itemName}</th>
                        <th>{attrNames.quantity}</th>
                        <th>{attrNames.unitOfMeasure}</th>
                    </tr>
                </thead>
                <tbody>
                    {salesLines.map(salesLine => (
                        <tr key={salesLine.data.salesLine.LineId}>
                            <td>{salesLine.data.salesLine.ItemId}</td>
                            <td>{salesLine.data.salesLine.Description}</td>
                            <td>{salesLine.data.salesLine.Quantity}</td>
                            <td>{salesLine.data.salesLine.UnitOfMeasureSymbol}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        {!delivery.showTimeslot && address}
        {delivery.showTimeslot && (
            <Node {...delivery.pickupProps}>
                {address}
                {delivery.pickupDateTimeslot}
            </Node>
        )}

        {salesLines[0].data.salesLine.AttributeValues?.find(attribute => {
            return attribute.Name === attrNames.attributeValueShipmentLineDetails
        }) ?
            Object.entries(
                salesLines.reduce<Record<string, typeof salesLines>>((acc, salesLine) => {
                    const jsonString = (salesLine.data.salesLine.AttributeValues?.find(attribute => {
                        return attribute.Name === attrNames.attributeValueShipmentLineDetails
                    }) as AttributeValue)?.TextValue;
                    const parsedArray = jsonString ? JSON.parse(jsonString) : [];
                    const shipmentId = (parsedArray.length > 0) ? (parsedArray[0]['SI'] || 'No Shipment') : 'No Shipment';
                    if (!acc[shipmentId]) {
                        acc[shipmentId] = [];
                    }
                    acc[shipmentId].push(salesLine);
                    return acc;
                }, {})
            ).map(([shipmentId, salesLinesGroup]) => {
                const jsonString = (salesLinesGroup[0].data.salesLine.AttributeValues?.find(attr => {
                    return attr.Name === attrNames.attributeValueShipmentLineDetails
                }) as AttributeValue).TextValue;
                //Accounts for when there is a product attribute set but is blank
                const parsedArray = jsonString ? JSON.parse(jsonString) : [];
                if (parsedArray.length <= 0) {
                    return <></>
                }
                return (
                    <div key={shipmentId} className={cls('shipment')}>
                        {/* @FIXME: Please update text to be resources */}
                        <div className={cls('shipment-header')}>
                            <div className={cls('shipment-title')}>Shipment ID: {parsedArray[0]['SI']}</div>
                        </div>
                        <div className={cls('shipment-content')}>
                            <div className={cls('shipment-details')}>
                                <div className={cls('shipment-line')}>
                                    <span className={cls('shipment-label')}>Shipping Method</span>
                                    <span className={cls('shipment-value')}>{parsedArray[0]['SM']}</span>
                                </div>
                                <div className={cls('shipment-line')}>
                                    <span className={cls('shipment-label')}>Track & trace No.</span>
                                    <span className={cls('shipment-value')}>
                                        <a href={trackingLinks?.find(link => { return link.ShippingMethod === parsedArray[0]['SM'] })?.TrackingLink + parsedArray[0]['TR']}>{parsedArray[0]['TR']}</a>
                                    </span>
                                </div>
                                <div className={cls('shipment-line')}>
                                    <span className={cls('shipment-label')}>Shipping Date</span>
                                    <span className={cls('shipment-value')}>{parsedArray[0]['SD']}</span>
                                </div>
                            </div>
                            <div className={cls('table-container')}>
                                <table className={cls('table')}>
                                    <thead>
                                        <tr>
                                            <th>{attrNames.itemNumber}</th>
                                            <th>{attrNames.itemName}</th>
                                            <th>{attrNames.quantity}</th>
                                            <th>{attrNames.unitOfMeasure}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {salesLinesGroup.map(salesLine => (
                                            <tr key={salesLine.data.salesLine.LineId}>
                                                <td>{salesLine.data.salesLine.ItemId}</td>
                                                <td>{salesLine.data.salesLine.Description}</td>
                                                <td>{salesLine.data.salesLine.Quantity}</td>
                                                <td>{salesLine.data.salesLine.UnitOfMeasureSymbol}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )
            }) : <></>}
    </Node>
);

export const OrderDetailsGroups: React.FC<IGroups & { trackingLinks?: ITrackingLinksData[] }> = ({ groupsProps, groups, trackingLinks }) => (
    <Node {...groupsProps}>
        {groups.map((group: IGroup, index: number) => (
            <OrderDetailsGroup key={index} {...group} trackingLinks={trackingLinks} />
        ))}
    </Node>
);

export const OrderDetailsOrderSummary: React.FC<IOrderSummary> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints
}) => (
    <Node {...orderSummaryProps}>
        {heading}
        {subtotal}
        {shipping}
        {tax}
        {totalAmount}
        {earnedPoints}
    </Node>
);

export const OrderDetailsPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const OrderDetailsHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

//@CUSTOM component that will display the customer purchase order number
export const OrderDetailsPONumber: React.FC<{ poNumber?: string }> = ({ poNumber }) => (
    <div className={cls('order-information-po-number')}>
        <span className={cls('po-number-label')}>Customer Purchase Order: </span>
        <span className={cls('po-number-value')}>{poNumber}</span>
    </div>
);

const OrderDetailsView: React.FC<IOrderDetailsViewProps & IOrderDetailsProps<{}>> = ({
    moduleProps,
    tableViewActions,
    viewModes,
    table,
    placedBy,
    heading,
    alert,
    loading,
    orderInfomation,
    orderSummary,
    payment,
    help,
    groups,
    config,
    data
}) => {
    const poNumber = (data?.orderHydration?.result?.salesOrder?.AttributeValues?.find(atr => {
        return atr.Name === attrNames.poNumber;
    }) as AttributeValue)?.TextValue || data?.orderHydration?.result?.salesOrder.CustomerRequisition || '';

    return (
        <Module {...moduleProps}>
            {placedBy}
            {heading}
            {alert}
            {loading}
            {orderInfomation && <OrderDetailsOrderInfomation {...orderInfomation} poNumber={poNumber} />}
            {tableViewActions}
            {viewModes}
            {table}
            {groups && <OrderDetailsGroups {...groups} trackingLinks={config.trackingLinks} />}
            {orderSummary && <OrderDetailsOrderSummary {...orderSummary} />}
            {payment && <OrderDetailsPayment {...payment} />}
            {help && <OrderDetailsHelp {...help} />}
        </Module>
    );
};

export default OrderDetailsView;
